import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { useProjectFormController } from '../hooks/useProjectFormController';
import { ProjectFormFields } from './ProjectFormFields';
import type { Project } from '../types';

interface ProjectFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project?: Project;
}

export function ProjectFormDialog({ open, onOpenChange, project }: ProjectFormDialogProps) {
  const { t } = useTranslation('projects');
  const { control, onSubmit, isPending, selectedColor, setValue, isEdit } =
    useProjectFormController({ project, open, onSuccess: () => onOpenChange(false) });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-full rounded-none md:max-w-[440px] md:rounded-lg"
        data-testid="projects-create-dialog"
      >
        <DialogHeader>
          <DialogTitle>{isEdit ? t('dialog.editTitle') : t('dialog.createTitle')}</DialogTitle>
          <DialogDescription className="sr-only">{t('dialog.description')}</DialogDescription>
        </DialogHeader>

        <form id="project-form" onSubmit={onSubmit} className="flex flex-col gap-5 py-2">
          <ProjectFormFields
            control={control}
            setValue={setValue}
            selectedColor={selectedColor}
            isPending={isPending}
          />
        </form>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
            data-testid="projects-cancel-button"
          >
            {t('dialog.cancel')}
          </Button>
          <Button
            type="submit"
            form="project-form"
            disabled={isPending}
            data-testid="projects-submit-button"
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isPending ? t('dialog.submitting') : t('dialog.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
